import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Loader } from "@/components/brand/Mascot";
import { Brush } from "@/components/brand/Lettering";
import { Countdown } from "@/components/Countdown";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { SignupForm } from "@/components/SignupForm";
import { WaitlistForm } from "@/components/WaitlistForm";
import { getDrop, getDropAccess, getProducts } from "@/lib/api";
import { checkAccess, dropIsLive, saveToken } from "@/lib/access";
import { abs, breadcrumb, origin } from "@/lib/site";
import NotFound from "@/pages/NotFound";

const ACCENT = ["bg-lime", "bg-pink", "bg-sky"];

const statusOf = (d, access) => {
  if (d.status === "sold_out") return ["Sold out", "bg-cloud"];
  if (dropIsLive(d)) return ["Live", "bg-lime"];
  if (access?.valid) return ["Early access", "bg-pink"];
  return [d.status === "upcoming" ? "Upcoming" : "Coming soon", "bg-cloud"];
};

const eventLd = (d, products) => ({
  "@context": "https://schema.org", "@type": "Event", name: `the suspects Drop ${d.number}: ${d.name}`, description: d.description,
  startDate: d.release_date, eventStatus: "https://schema.org/EventScheduled", eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
  location: { "@type": "VirtualLocation", url: abs(`/drops/${d.slug}`) }, image: [abs(d.image)],
  organizer: { "@type": "Organization", name: "the suspects", url: origin() },
  offers: products.map((p) => ({ "@type": "Offer", url: abs(`/product/${p.slug}`), price: p.price.toFixed(2), priceCurrency: "CAD", availability: dropIsLive(d) ? "https://schema.org/InStock" : "https://schema.org/PreOrder", validFrom: d.release_date })),
});
const listLd = (d, products) => ({
  "@context": "https://schema.org", "@type": "CollectionPage", name: `Drop ${d.number}: ${d.name}`, url: abs(`/drops/${d.slug}`),
  mainEntity: { "@type": "ItemList", itemListElement: products.map((p, i) => ({ "@type": "ListItem", position: i + 1, url: abs(`/product/${p.slug}`), name: p.name })) },
});

const EarlyBanner = ({ d, access }) => {
  if (dropIsLive(d) || d.status === "sold_out") return null;
  if (access?.valid) return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-[1.5px] border-ink bg-pink px-6 py-5" data-testid="early-access-in">
      <p className="display text-3xl">Crew early access: you're in</p>
      <p className="label font-bold">Unlocked for {access.email} · add to cart is open</p>
    </div>
  );
  return (
    <div className="grid items-center gap-6 border-[1.5px] border-ink bg-cloud px-6 py-6 lg:grid-cols-2" data-testid="early-access-pitch">
      <div>
        <p className="display text-3xl">Crew gets in {d.early_access_minutes >= 60 ? `${Math.round(d.early_access_minutes / 60)} hour${d.early_access_minutes >= 120 ? "s" : ""}` : `${d.early_access_minutes} minutes`} early.</p>
        <p className="mt-2 text-[14px] text-ink/70">Join the Sus Crew and we'll email you a personal early-access link before it goes public.{access && !access.valid && access.reason === "not_open_yet" ? " Your link unlocks when the early window opens." : ""}</p>
      </div>
      <SignupForm source={`drop-${d.slug}`} testid="drop-crew-signup" />
    </div>
  );
};

export default function DropPage() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState(undefined);
  const [products, setProducts] = useState([]);
  const [access, setAccess] = useState(null);

  useEffect(() => {
    setD(undefined);
    getDrop(slug).then(setD).catch(() => setD(null));
    getProducts({ drop: slug }).then(setProducts).catch(() => setProducts([]));
  }, [slug]);

  useEffect(() => {
    const t = params.get("access");
    if (t) {
      getDropAccess(slug, t).then((a) => {
        if (a.valid || a.reason === "not_open_yet") { saveToken(slug, t); toast.success(a.valid ? "Crew early access unlocked 👀" : "Link saved. It unlocks when the early window opens."); }
        else toast.error("That early access link is expired or invalid.");
        setAccess(a);
        params.delete("access"); setParams(params, { replace: true });
      }).catch(() => toast.error("Couldn't check that link."));
    } else checkAccess(slug).then(setAccess);
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (d === undefined) return <Loader label="Loading the drop" />;
  if (d === null) return <NotFound />;
  const [badge, badgeBg] = statusOf(d, access);
  const accent = ACCENT[(parseInt(d.number, 10) - 1) % 3];
  const live = dropIsLive(d);
  const preview = !live && !access?.valid;
  return (
    <div data-testid="drop-page">
      <Seo title={`Drop ${d.number}: ${d.name}`} description={`${d.description} ${live ? "" : `Drops ${d.release_label}.`}`} path={`/drops/${d.slug}`} image={d.image}
        jsonLd={[eventLd(d, products), listLd(d, products), breadcrumb([{ name: "Home", path: "/" }, { name: "Drops", path: "/drops" }, { name: d.name, path: `/drops/${d.slug}` }])]} />
      <section className={`relative overflow-hidden border-b-[1.5px] border-ink ${accent}`} data-testid="drop-hero">
        <div className="mx-auto grid max-w-[1440px] items-end gap-10 px-5 pb-14 pt-14 sm:px-10 lg:grid-cols-12 lg:pb-20 lg:pt-20">
          <div className="lg:col-span-7">
            <div className="mb-4 flex items-center gap-4"><span className="label font-bold">Drop</span><span className={`sticker ${badgeBg} !text-xs`} data-testid="drop-status-badge">{badge}</span></div>
            <p className="display text-[clamp(8rem,24vw,20rem)] leading-[0.8]" data-testid="drop-number">{d.number}</p>
            <h1 className="display mt-4 text-[clamp(3rem,7vw,6.5rem)]" data-testid="drop-name">{d.name}</h1>
            <Brush lines={[d.tagline]} className="mt-6 text-[clamp(2rem,4vw,3.5rem)]" testid="drop-tagline" />
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden rounded-[24px] border-[1.5px] border-ink bg-cloud"><img src={d.image} alt={`Drop ${d.number}, ${d.name} campaign photo`} className="photo h-full w-full object-cover" /></div>
          </div>
        </div>
      </section>
      <div className="mx-auto max-w-[1440px] space-y-20 px-5 py-20 sm:px-10">
        <EarlyBanner d={d} access={access} />
        <section className="grid gap-12 lg:grid-cols-12" aria-labelledby="drop-story-title">
          <Reveal className="lg:col-span-5">
            <h2 id="drop-story-title" className="label font-bold">The story</h2>
            <p className="mt-6 text-[clamp(1.15rem,1.6vw,1.4rem)] leading-relaxed" data-testid="drop-story">{d.story || d.description}</p>
            <p className="label mt-8 text-ink/60">{live ? (d.status === "sold_out" ? `Released ${d.release_label}` : "Live now") : `Drops ${d.release_label}`}</p>
          </Reveal>
          <div className={`grid gap-4 lg:col-span-7 ${d.lifestyle_images.length > 1 ? "grid-cols-2" : ""}`}>
            {d.lifestyle_images.map((im, i) => <Reveal key={im.src} delay={i * 90}><img src={im.src} alt={im.alt} loading="lazy" className={`photo w-full rounded-[20px] object-cover ${i ? "mt-16 aspect-[3/4]" : "aspect-[3/4]"}`} data-testid={`drop-lifestyle-${i}`} /></Reveal>)}
          </div>
        </section>
        {!live && (
          <section className="grid items-center gap-10 rounded-[24px] border-[1.5px] border-ink bg-sky p-6 sm:p-10 lg:grid-cols-2" aria-label="Countdown and waitlist" data-testid="drop-countdown-block">
            <div><p className="label font-bold">Dropping in</p><div className="mt-4"><Countdown target={d.release_date} size="sm" testid="drop-countdown" /></div></div>
            {["coming_soon", "upcoming"].includes(d.status) && <WaitlistForm drop={d} testid="drop-page-waitlist" />}
          </section>
        )}
        <section aria-labelledby="drop-products-title">
          <div className="flex flex-wrap items-end justify-between gap-4 border-b border-ink/10 pb-6">
            <h2 id="drop-products-title" className="display text-[clamp(2.75rem,5vw,4.5rem)]">The pieces</h2>
            {preview && products.length > 0 && <span className="label text-ink/60" data-testid="drop-preview-note">Preview · add to cart opens {d.release_label}</span>}
            {live && d.status !== "sold_out" && <Link to={`/shop?drop=${d.slug}`} className="link-u" data-testid="drop-shop-all">Shop all <ArrowRight size={14} /></Link>}
          </div>
          {products.length === 0 ? <p className="py-14 text-ink/60" data-testid="drop-products-empty">Pieces are under wraps. Join the waitlist to see them first.</p> : (
            <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-6 lg:grid-cols-4" data-testid="drop-products-grid">
              {products.map((p, i) => <div key={p.slug} className="relative">{preview && <span className="sticker absolute right-3 top-3 z-10 bg-cloud" data-testid={`drop-preview-${p.slug}`}>Preview</span>}<ProductCard product={p} index={i} /></div>)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
